import React, { useRef } from "react";
import styled from "styled-components";
import { Button } from "antd";
import { HeartOutlined, StarFilled, LeftOutlined, RightOutlined } from "@ant-design/icons";
import { popularExperiences, popularImages } from "./Common";

const PopularExperience = styled.div`
  padding-top: 7rem;
  padding-left: 8rem;
  padding-right: 8rem;
  display: flex;
  flex-direction: column;
  gap: 2rem;
`;

const SmallHeader = styled.h2`
  text-align: left;
  margin: 0;
  font-size: 28px;
  font-weight: 700;
  line-height: 100%;
`;

const PopularExperienceContainer = styled.div`
  display: flex;
  overflow-x: auto;
  gap: 1.5rem;
  padding-bottom: 1rem;
  scroll-behavior: smooth;
  &::-webkit-scrollbar {
    display: none;
  }
`;

const PopularCardWrapper = styled.div`
  min-width: calc(20% - 1.2rem);
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const PopularCard = styled.div`
  border-radius: 16px;
  height: 250px;
  position: relative;
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
  overflow: hidden;

  &::before {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(180deg, rgba(0, 0, 0, 0) 0%, rgba(0, 0, 0, 0.7) 100%);
    border-radius: 16px;
  }
`;

const PopularCardTop = styled.div`
  display: flex;
  justify-content: space-between;
  padding: 1rem;
  position: relative;
  z-index: 2;
`;

const PopularCardBottom = styled.div`
  padding: 0 0.5rem;
  color: #333;
`;

const NavigationButtons = styled.div`
  display: flex;
  justify-content: right;
  gap: 1rem;
  margin-top: 1rem;
`;

const NavButton = styled(Button)`
  height: 30px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #f0f0f0;
  border: 1px solid #d9d9d9;

  &:hover {
    background: #e0e0e0;
  }
`;

const CategoryTag = styled(Button)`
  background: #f0f0f0;
  color: #333;
  border: none;
  border-radius: 20px;
  font-weight: 500;
  font-size: 12px;
  padding: 0 12px;
  height: 24px;
  display: flex;
  align-items: center;
`;

const LikeButton = styled(Button)`
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: rgba(240, 240, 240, 0.9);
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1px solid #333;

  .anticon {
    color: #333;
    font-size: 16px;
  }
`;

const ExperienceTitle = styled.h3`
  color: #333;
  font-size: 18px;
  font-weight: 700;
  margin: 0 0 8px 0;
`;

const PriceRating = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const Price = styled.span`
  color: #333;
  font-size: 14px;
  font-weight: 500;
`;

const Rating = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
`;

const RatingNumber = styled.span`
  color: #333;
  font-size: 14px;
  font-weight: 500;
`;

const PopularExperiences: React.FC = () => {
  const scrollRef = useRef<HTMLDivElement>(null);

  const scroll = (direction: "left" | "right") => {
    if (scrollRef.current) {
      const scrollAmount = direction === "left" ? -300 : 300;
      scrollRef.current.scrollBy({ left: scrollAmount, behavior: "smooth" });
    }
  };

  return (
    <PopularExperience>
      <SmallHeader>Popular Experiences</SmallHeader>
      <PopularExperienceContainer ref={scrollRef}>
        {popularExperiences.map((experience, index) => (
          <PopularCardWrapper key={index}>
            <PopularCard style={{ backgroundImage: `url(${popularImages[index]})` }}>
              <PopularCardTop>
                <CategoryTag size="small">{experience.category}</CategoryTag>
                <LikeButton icon={<HeartOutlined />} />
              </PopularCardTop>
            </PopularCard>
            <PopularCardBottom>
              <ExperienceTitle>{experience.title}</ExperienceTitle>
              <PriceRating>
                <Price>From ${experience.price}/guest</Price>
                <Rating>
                  <StarFilled style={{ color: "#FFD700" }} />
                  <RatingNumber>{experience.rating}</RatingNumber>
                </Rating>
              </PriceRating>
            </PopularCardBottom>
          </PopularCardWrapper>
        ))}
      </PopularExperienceContainer>
      <NavigationButtons>
        <NavButton icon={<LeftOutlined />} onClick={() => scroll("left")} />
        <NavButton icon={<RightOutlined />} onClick={() => scroll("right")} />
      </NavigationButtons>
    </PopularExperience>
  );
};

export default PopularExperiences;
